import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { useCustomerTickets } from "../api/support";
import CreateTicketDialog from "../components/CreateTicketDialog";

function CustomerSupport() {
  const { t } = useTranslation("support");
  const [createTicketDialog, setCreateTicketDialog] = useState(false);

  const {
    tickets,
    loading,
    error: ticketsError,
    refetch: refetchTickets,
  } = useCustomerTickets();

  useEffect(() => {
    if (ticketsError) {
      toast.error(t("errors.loadFailed"));
      console.error("Error fetching tickets:", ticketsError);
    }
  }, [ticketsError, t]);

  const handleTicketCreated = () => {
    setCreateTicketDialog(false);
    refetchTickets();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "warning";
      case "in_progress":
        return "info";
      case "resolved":
        return "success";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("page.title")}
        description={t("page.description")}
        appendActions={
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={() => setCreateTicketDialog(true)}
          >
            {t("page.newTicket")}
          </Button>
        }
      />

      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("table.ticketNumber")}</TableCell>
              <TableCell>{t("table.subject")}</TableCell>
              <TableCell>{t("table.status")}</TableCell>
              <TableCell>{t("table.priority")}</TableCell>
              <TableCell>{t("table.created")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="bodySm" color="text.secondary">
                    {t("table.noTickets")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => (
                <TableRow key={ticket.id} hover>
                  <TableCell>
                    <Typography variant="bodySm" fontWeight="medium">
                      {ticket.ticket_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">{ticket.subject}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        ticket.status.charAt(0).toUpperCase() +
                        ticket.status.slice(1).replace("_", " ")
                      }
                      color={getStatusColor(ticket.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        ticket.priority.charAt(0).toUpperCase() +
                        ticket.priority.slice(1)
                      }
                      color={getPriorityColor(ticket.priority)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm" color="text.secondary">
                      {formatDate(ticket.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateTicketDialog
        open={createTicketDialog}
        onClose={() => setCreateTicketDialog(false)}
        onTicketCreated={handleTicketCreated}
      />
    </Box>
  );
}

export default CustomerSupport;
